import { Button } from '@forgeglobal/design-framework';
import { NavigatorExternalLink } from '@forgeglobal/design-framework/hooks/navigator/NavigatorExternalLink';
import {
  Notification,
  NotificationContext,
} from '@tcs/client-portal-common/providers/NotificationProvider';
import { getConfig } from '@tcs/client-portal/config/configService';
import { Status } from '@tcs/common/types';
import { first } from 'lodash';
import { useContext, useEffect, useRef } from 'react';
import useFees from './useFees';
import { FeeDetails } from '@tcs/client-portal-api/types/Fees';

const useFeesNotifications = () => {
  const payFeesButtonRef = useRef<HTMLDivElement>(null);

  const { addNotifications } = useContext(NotificationContext);

  const { data: fees, loading } = useFees();

  const payLink = () => {
    return (
      <div ref={payFeesButtonRef} hidden>
        <NavigatorExternalLink
          modalTitle={'Redirecting to the Payment Portal'}
          modalMessage={
            'You will be taken to a 3rd party payment portal in a separate tab. Would you like to continue?'
          }
          linkName={'Pay Account Fees'}
          to={`${getConfig().REACT_APP_BASE_PATH}/fees`}
        />
      </div>
    );
  };

  const getFeeStatus = (unpaidFeeCollectionDate) => {
    const now = new Date();
    const due = new Date(unpaidFeeCollectionDate);
    if (due < now) {
      return 'error';
    }
    return 'warning';
  };

  const getNotification = ({
    status,
    feeTotal,
    accountNumber,
  }: {
    status: Status;
    feeTotal: number;
    accountNumber?: string;
  }) => ({
    text: `There is currently a balance of $${feeTotal} due on your account(s). Login to our payment portal to view details on this outstanding amount and make an online payment. Please allow 48 hours for payments to be reflected online.`,
    status,
    action: (
      <div>
        <Button
          onClick={() => {
            const childElement = payFeesButtonRef.current
              ?.children[0] as HTMLElement;
            if (childElement) {
              childElement.click();
            }
          }}
          variant="text"
          color="primary"
          data-test-id="fee-due-banner-button">
          Pay Account Fees
        </Button>
        {payLink()}
      </div>
    ),
    type: 'fee',
    id: accountNumber,
  });

  const isValidFees = (fee: FeeDetails) => {
    if (fee.PaidDate == undefined) {
      if (
        fee.FeePayorType == undefined ||
        fee.FeePayorType.toLowerCase() === 'investor'
      ) {
        return true;
      }
      return false;
    }
    return false;
  };

  useEffect(() => {
    if (loading) {
      return;
    }

    var accountFeesTotal = 0;
    var accountFeesStatus: Status = 'warning';

    var notifications = fees?.reduce<Notification[]>(
      (accumulator, accountFees) => {
        const investorFees = accountFees.filter((fee) => isValidFees(fee));

        const feeTotal = investorFees.reduce(
          (sum, { Amount }) => sum + Amount,
          0
        );

        if (feeTotal === 0) {
          return accumulator;
        }

        accountFeesTotal += feeTotal;

        const fee = first(investorFees);

        const status = getFeeStatus(fee.CollectionDate);

        if (status === 'error') {
          accountFeesStatus = status;
        }

        return [
          ...accumulator,
          getNotification({
            status,
            feeTotal,
            accountNumber: fee.AccountNumber,
          }),
        ];
      },
      []
    );

    if (accountFeesTotal > 0) {
      notifications = [
        getNotification({
          status: accountFeesStatus,
          feeTotal: accountFeesTotal,
        }),
        ...notifications,
      ];
    }

    if (notifications && notifications.length > 0) {
      addNotifications(notifications, true, true);
    }
  }, [loading, fees]);
};

export default useFeesNotifications;
